import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dropdown, Tooltip } from 'antd';
import type { ItemType } from 'antd/lib/menu/hooks/useItems';
import { AssetItemDto } from '@api/Api';
import { assetGetAssetsDownloadList } from '@api/Asset';
import { apiClient } from '@api/client';
import { useCopyAssetsMutation } from '@hooks/queries/assets';
import {
  deleteAssetAction,
  restoreDeletedAssetAction
} from '@redux/actions/assetsAction';
import {
  toggleShareAssetModal,
  toggleApprovalModal,
  toggleApprovalConflictAssetsModal,
  toggleCopyModal,
  toggleMoveModal,
  toggleAddToExistingLinkModal
} from '@redux/actions/modalAction';
import { useCurrentWorkspace } from '@hooks/workspace';
import { downloadAll } from '@helpers/download';
import { useOrganization } from '@components/OrganizationBoundary';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import useTypedDispatch from '@hooks/useTypedDispatch';
import {
  ActionIcon,
  ApprovalRoutingIcon,
  DownloadInvoiceIcon,
  DeleteIcon,
  DiamondUpgradeIcon,
  DropdownCopyIcon,
  DropdownDuplicateIcon,
  DropdownLinkIcon,
  DropdownLinkPlusIcon
} from '@icons';
import './BulkActions.less';

async function fetchFolderAssets(folder: AssetItemDto) {
  const assets: AssetItemDto[] = [];
  let after: string | undefined;
  let hasNext = true;

  while (hasNext) {
    const { data } = await apiClient.asset.assetControllerListAssets({
      workspaceId: folder.workspaceId,
      campaignId: folder.campaignId,
      parentId: folder.id,
      isFolder: false,
      flatten: true,
      limit: 100,
      after
    });
    assets.push(
      ...data.edges
        .filter((edge) => !edge.node.isFolder)
        .map((edge) => edge.node)
    );
    hasNext = data.hasNext;
    after = data.endCursor ?? undefined;
  }

  return assets;
}

type BulkActionsProps = {
  selectedAssets: { node: AssetItemDto; cursor: string }[];
  selectedFolders?: { node: AssetItemDto; cursor: string }[];
  canShare: boolean;
  onClose: () => void;
  page: string;
  onRefreshAssetList?: () => void;
};

function BulkActions({
  selectedAssets,
  selectedFolders = [],
  canShare,
  onClose,
  page,
  onRefreshAssetList
}: BulkActionsProps) {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const canCreateAw = !!currentWorkspace?.permissions.createApprovalWorkflows;
  const { currentOrganization } = useOrganization();
  const awFeatureEnabled =
    !!currentOrganization.entity?.features.approvalRouting;
  const [isLoadingApproval, setIsLoadingApproval] = useState(false);

  const { mutateAsync: copyAssets, isPending: isCopyingAssets } =
    useCopyAssetsMutation({});

  const selectedItems = [...selectedAssets, ...selectedFolders];
  const selectedCount = selectedItems.length;

  const canSameCampaign = useMemo(() => {
    if (!selectedAssets.length && !selectedFolders.length) return false;
    return [...selectedAssets, ...selectedFolders]
      .map(({ node }) => node.campaignId)
      .every((it, _idx, array) => it === array[0]);
  }, [selectedAssets, selectedFolders]);

  const sameCampaignTooltip = !canSameCampaign
    ? 'Select items from the same project to use this action.'
    : '';

  const { canBulkDelete, canBulkDownload, canBulkApprovalWorkflow } =
    useMemo(() => {
      const items = [...selectedAssets, ...selectedFolders];
      if (!items.length)
        return {
          canBulkDownload: false,
          canBulkDelete: false,
          canBulkApprovalWorkflow: false
        };

      const canBulkDownload: boolean = items.every(({ node }) =>
        node.folder
          ? !!node.folder.totalAssetsCount
          : !!node.asset?.permissions.download
      );
      const canBulkDelete: boolean = items.every(({ node }) =>
        node.folder
          ? !!node.folder.permissions?.delete
          : !!node.asset?.permissions.delete
      );
      const canBulkApprovalWorkflow =
        selectedFolders.length === 0 &&
        selectedAssets.every(
          (asset: { node: AssetItemDto; cursor: string }) =>
            !asset?.node?.asset?.approvalWorkflow
        );

      return {
        canBulkDownload,
        canBulkDelete,
        canBulkApprovalWorkflow
      };
    }, [selectedAssets, selectedFolders]);

  const upgradeShare =
    selectedCount > 1 &&
    !currentOrganization.entity?.features.shareMultipleAssets;
  const canUseBatchApprovals =
    !!currentOrganization.entity?.features.batchApprovals ||
    selectedCount === 1;

  const createApprovalRoutingTooltipText = useMemo(() => {
    if (sameCampaignTooltip) return '';
    if (!awFeatureEnabled) {
      return ' to a paid plan to access this feature.';
    }
    if (!canUseBatchApprovals) {
      return ' to an Enterprise plan to route multiple media assets for approval at one time.';
    }
    if (selectedFolders.length === 0 && !canBulkApprovalWorkflow) {
      return selectedCount === 1
        ? 'Approval routing is active for the selected file.'
        : 'Approval routing is active for some of the selected files.';
    }
    return '';
  }, [
    sameCampaignTooltip,
    awFeatureEnabled,
    canUseBatchApprovals,
    canBulkApprovalWorkflow,
    selectedFolders.length,
    selectedCount
  ]);

  const selectionCopy = [
    selectedAssets.length ? `${selectedAssets.length} media` : null,
    selectedFolders.length
      ? `${selectedFolders.length} ${
          selectedFolders.length === 1 ? 'folder' : 'folders'
        }`
      : null
  ]
    .filter(Boolean)
    .join(', ');

  const menuItems: ItemType[] = [
    {
      key: 'duplicate-folder',
      label: 'Duplicate',
      disabled: isCopyingAssets,
      icon: <DropdownDuplicateIcon />,
      onClick: async () => {
        if (!selectedItems.length) return;
        const first = selectedItems[0];
        await copyAssets({
          workspaceId: first.node.workspaceId,
          assetIds: selectedItems.map((item) => item.node.id),
          targetCampaignId: first.node.campaignId,
          targetFolderId: first.node.path.slice(-1)[0]?.id,
          copyComments: true,
          ...(selectedFolders.length
            ? {
                folderSettings: {
                  copyAssets: true,
                  includeMembers: true
                }
              }
            : {})
        });
      }
    },
    {
      key: 'move_to',
      label: (
        <Tooltip
          title={sameCampaignTooltip || undefined}
          overlayClassName="toggle_favorite_overlay"
        >
          <span>Move to</span>
        </Tooltip>
      ),
      disabled: !canSameCampaign,
      icon: <DropdownCopyIcon />,
      onClick: () => {
        if (!canSameCampaign || !selectedItems.length) return;
        const first = selectedItems[0];
        dispatch(
          toggleMoveModal({
            visible: true,
            assets: selectedAssets.map((a) => ({
              id: a.node.id,
              name: `${a.node.asset?.versions[0]?.name}.${a.node.asset?.versions[0]?.extension}`
            })),
            folders: selectedFolders.map((folder) => ({
              id: folder.node.id,
              name: folder.node.folder?.name ?? ''
            })),
            campaignId: first.node.campaignId,
            campaignName: first.node.campaignName,
            permissions: {
              moveToCampaign: selectedItems.every(({ node }) =>
                node.folder
                  ? !!node.folder.permissions?.moveToCampaign
                  : !!node.asset?.permissions.moveToCampaign
              ),
              moveToPublicFolder: selectedItems.every(({ node }) =>
                node.folder
                  ? !!node.folder.permissions?.moveToPublicFolder
                  : !!node.asset?.permissions.moveToPublicFolder
              ),
              moveToPrivateFolder: selectedItems.every(({ node }) =>
                node.folder
                  ? !!node.folder.permissions?.moveToPrivateFolder
                  : !!node.asset?.permissions.moveToPrivateFolder
              )
            }
          })
        );
      }
    },
    ...(!canShare || upgradeShare
      ? []
      : [
          {
            type: 'divider' as const
          },
          {
            key: 'add-to-existing-link',
            label: (
              <Tooltip
                title={sameCampaignTooltip || undefined}
                overlayClassName="toggle_favorite_overlay"
              >
                <span>Add to existing link</span>
              </Tooltip>
            ),
            disabled: !canSameCampaign,
            icon: <DropdownLinkIcon />,
            onClick: () => {
              if (!canSameCampaign || !selectedItems.length) return;
              dispatch(
                toggleAddToExistingLinkModal({
                  visible: true,
                  campaignId: selectedItems[0].node.campaignId,
                  assets: selectedAssets.map((asset) => ({
                    id: asset.node.id,
                    versionId: asset.node.asset?.versions[0]?.id
                  })),
                  folders: selectedFolders.map((folder) => folder.node.id)
                })
              );
            }
          }
        ]),

    ...(canBulkDelete
      ? [
          {
            type: 'divider' as const
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteIcon />,
            onClick: () => {
              if (!canBulkDelete) return;
              dispatch(
                deleteAssetAction({
                  assetId: selectedItems.map((item) => item.node.id),
                  permanent: false
                })
              );
            }
          }
        ]
      : [])
  ];

  return (
    <div
      className={
        selectedCount > 0
          ? 'bulk_actions_container bulk_actions_container_active'
          : 'bulk_actions_container'
      }
    >
      <div className="bulk_actions_container__body">
        <div className="items">
          <div className="list" />
          <div className="copy">
            {selectionCopy ? `${selectionCopy} selected` : '0 selected'}
          </div>
        </div>
        <div className="close" onClick={onClose} />
      </div>
      <div className="bulk_actions_container__actions">
        {page === 'deleted' ? (
          <>
            <Button
              type="text"
              size="small"
              className="action"
              disabled={!canBulkDelete}
              onClick={async () => {
                if (!canBulkDelete) return;
                await dispatch(
                  deleteAssetAction({
                    assetId: selectedItems.map((item) => item.node.id),
                    permanent: true
                  })
                );
                onClose();
              }}
            >
              <DeleteIcon style={{ fontSize: 24 }} /> Delete forever
            </Button>
            <Button
              type="text"
              size="small"
              className="action"
              disabled={!canBulkDelete}
              onClick={async () => {
                if (!canBulkDelete) return;
                await dispatch(
                  restoreDeletedAssetAction({
                    assetId: selectedItems.map((item) => item.node.id)
                  })
                );
                onClose();
              }}
            >
              Restore
            </Button>
          </>
        ) : (
          <>
            <Dropdown
              trigger={['click']}
              overlayClassName="asset_links_overlay"
              menu={{
                items: menuItems,
                onClick: () => {
                  onClose();
                }
              }}
            >
              <Button type="text" size="small" className="action">
                <ActionIcon className="action__icon" />
                More
              </Button>
            </Dropdown>
            <UpgradePlanTooltip
              text=" to a Team plan to access this feature."
              placement="left"
              isCanVisible={upgradeShare && !sameCampaignTooltip}
            >
              <Tooltip
                title={sameCampaignTooltip || undefined}
                overlayClassName="toggle_favorite_overlay"
              >
                <span>
                  <Button
                    type="text"
                    size="small"
                    className="action"
                    disabled={
                      !canShare ||
                      upgradeShare ||
                      !canSameCampaign ||
                      !selectedCount
                    }
                    onClick={() => {
                      if (!canSameCampaign || !selectedItems.length) return;
                      dispatch(
                        toggleShareAssetModal({
                          visible: true,
                          campaignId: selectedItems[0].node.campaignId,
                          assets: selectedAssets.map((it) => ({
                            id: it.node.id,
                            versionId: it.node.asset?.versions[0]?.id
                          })),
                          folders: selectedFolders.map(
                            (folder) => folder.node.id
                          ),
                          defaultName:
                            selectedFolders.length === 1 &&
                            !selectedAssets.length
                              ? selectedFolders[0].node.folder?.name || ''
                              : undefined
                        })
                      );
                      onClose();
                    }}
                  >
                    {upgradeShare && !sameCampaignTooltip && (
                      <DiamondUpgradeIcon className="action__icon upgrade-icon" />
                    )}
                    {(!upgradeShare || !!sameCampaignTooltip) && (
                      <DropdownLinkPlusIcon className="action__icon" />
                    )}
                    New share link
                  </Button>
                </span>
              </Tooltip>
            </UpgradePlanTooltip>
            <Tooltip
              title={sameCampaignTooltip || undefined}
              overlayClassName="toggle_favorite_overlay"
            >
              <span>
                <Button
                  type="text"
                  size="small"
                  className="action"
                  disabled={!canSameCampaign}
                  onClick={() => {
                    if (!canSameCampaign || !selectedItems.length) return;
                    dispatch(
                      toggleCopyModal({
                        visible: true,
                        assets: selectedAssets.map((asset) => ({
                          id: asset.node.id,
                          name: `${asset.node.asset?.versions[0]?.name}.${asset.node.asset?.versions[0].extension}`
                        })),
                        folders: selectedFolders.map((folder) => ({
                          id: folder.node.id,
                          isPrivate: !!folder.node.isPrivate,
                          name: folder.node.folder?.name || ''
                        }))
                      })
                    );
                    onClose();
                  }}
                >
                  <DropdownCopyIcon className="action__icon" />
                  Copy to
                </Button>
              </span>
            </Tooltip>
            <UpgradePlanTooltip
              text={createApprovalRoutingTooltipText}
              placement="top"
              isCanVisible={
                !!createApprovalRoutingTooltipText && !sameCampaignTooltip
              }
              isShowUpgradeText={!canUseBatchApprovals || !awFeatureEnabled}
            >
              <Tooltip
                title={sameCampaignTooltip || undefined}
                overlayClassName="toggle_favorite_overlay"
              >
                <span>
                  <Button
                    type="text"
                    size="small"
                    className="action"
                    disabled={
                      !!sameCampaignTooltip ||
                      !!createApprovalRoutingTooltipText ||
                      isLoadingApproval
                    }
                    loading={isLoadingApproval}
                    onClick={async () => {
                      if (
                        !canSameCampaign ||
                        !canUseBatchApprovals ||
                        !awFeatureEnabled
                      )
                        return;

                      if (
                        selectedFolders.length === 1 &&
                        !selectedAssets.length
                      ) {
                        const folder = selectedFolders[0];
                        dispatch(
                          toggleApprovalConflictAssetsModal({
                            visible: true,
                            title: 'Route folder for approval',
                            campaignId: folder.node.campaignId,
                            workspaceId: folder.node.workspaceId,
                            parentId: folder.node.id,
                            callback: (aw) => {
                              onClose();
                              if (aw) {
                                navigate(
                                  `/campaigns/approvals/${aw.workspaceId}/${aw.campaignId}/${aw.id}`
                                );
                                return;
                              }
                              navigate(
                                `/campaigns/single/${folder.node.campaignId}?tab=approvals`
                              );
                            }
                          })
                        );
                        return;
                      }

                      const openApprovalModal = (
                        assets: AssetItemDto[],
                        campaignId: string
                      ) => {
                        const assetVersions = assets
                          .filter(
                            (item) =>
                              !item.asset?.approvalWorkflow &&
                              !!item.asset?.versions[0]
                          )
                          .map((item) => ({
                            ...item.asset!.versions[0],
                            assetId: item.id
                          }));
                        if (!assetVersions.length) return;
                        dispatch(
                          toggleApprovalModal({
                            visible: true,
                            assetVersions,
                            campaignId,
                            permissions: {
                              create: canCreateAw
                            },
                            callback: (aw) => {
                              onClose();
                              if (aw) {
                                navigate(
                                  `/campaigns/approvals/${aw.workspaceId}/${aw.campaignId}/${aw.id}`
                                );
                                return;
                              }
                              onRefreshAssetList?.();
                            }
                          })
                        );
                      };

                      if (selectedFolders.length) {
                        setIsLoadingApproval(true);
                        try {
                          const folderAssetsLists = await Promise.all(
                            selectedFolders.map(({ node }) =>
                              fetchFolderAssets(node)
                            )
                          );
                          const assetsById = new Map<string, AssetItemDto>();
                          for (const { node } of selectedAssets) {
                            assetsById.set(node.id, node);
                          }
                          for (const folderAssets of folderAssetsLists) {
                            for (const asset of folderAssets) {
                              assetsById.set(asset.id, asset);
                            }
                          }
                          openApprovalModal(
                            [...assetsById.values()],
                            selectedItems[0].node.campaignId
                          );
                        } finally {
                          setIsLoadingApproval(false);
                        }
                        return;
                      }

                      if (!canBulkApprovalWorkflow) return;
                      const campaignId = selectedAssets?.[0]?.node?.campaignId;
                      if (!campaignId) return;
                      openApprovalModal(
                        selectedAssets.map(({ node }) => node),
                        campaignId
                      );
                    }}
                  >
                    {!sameCampaignTooltip &&
                    (!canUseBatchApprovals || !awFeatureEnabled) ? (
                      <DiamondUpgradeIcon className="action__icon upgrade-icon" />
                    ) : (
                      <ApprovalRoutingIcon className="action__icon" />
                    )}
                    Create approval routing
                  </Button>
                </span>
              </Tooltip>
            </UpgradePlanTooltip>

            <Button
              type="text"
              size="small"
              className="action"
              disabled={!canBulkDownload}
              onClick={async () => {
                if (!canBulkDownload) return;
                if (selectedAssets.length) {
                  downloadAll(
                    selectedAssets.map((x) => ({
                      ...x.node,
                      path: []
                    }))
                  );
                }
                for (const folder of selectedFolders) {
                  if (!folder.node.folder) continue;
                  const {
                    data: { list }
                  } = await assetGetAssetsDownloadList({
                    campaignId: folder.node.campaignId,
                    workspaceId: folder.node.workspaceId,
                    parentId: folder.node.id
                  });
                  downloadAll(list, folder.node.folder.name, folder.node.id);
                }
                onClose();
              }}
            >
              <DownloadInvoiceIcon className="action__icon" />
              Download
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default BulkActions;
